import { Folder, FolderContent } from '@/api/generatedApi';
import { Ellipsis } from '@/components/Ellipsis';
import { FolderIcon } from '@/components/FolderIcon';
import { pluralize } from '@/utils/format';
import { Box, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { DND_NO_SELECT_STYLES, DND_OPACITY } from '@/components/dnd';
import { useBulkSelectionContext } from '@/components/ContentsTable/BulkSelectionContext';
import { memo, useMemo } from 'react';
import { InlineLink, MediaArea, StyledCard, StyledCardContent, ThumbnailLink } from './cardStyles';

const FolderCardVisual = memo(function FolderCardVisual({
  folder,
  linkTo,
}: {
  folder: Folder;
  linkTo: { pathname: string; search: string };
}) {
  const countsText = [
    folder.assetsCount !== undefined && pluralize(folder.assetsCount, 'asset', 'assets'),
    folder.foldersCount !== undefined && pluralize(folder.foldersCount, 'folder', 'folders'),
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <MediaArea>
        <ThumbnailLink to={linkTo} draggable={false} sx={{ width: 105, height: 105 }}>
          <Box sx={{ transform: 'scale(2.5)' }}>
            <FolderIcon />
          </Box>
        </ThumbnailLink>
      </MediaArea>
      <StyledCardContent>
        <InlineLink to={linkTo} draggable={false} sx={{ display: 'block', overflow: 'hidden', minWidth: 0 }}>
          <Typography
            variant='body2'
            component='span'
            sx={{
              fontWeight: 600,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {folder.name}
          </Typography>
        </InlineLink>
        {countsText && (
          <InlineLink to={linkTo} draggable={false}>
            <Typography variant='caption' color='text.secondary' component='span'>
              {countsText}
            </Typography>
          </InlineLink>
        )}
        {folder.description && (
          <Ellipsis
            text={folder.description}
            sx={{ fontSize: '0.75rem', opacity: 0.6 }}
            tooltipProps={{ placement: 'bottom-start' }}
          />
        )}
      </StyledCardContent>
    </>
  );
});

export const FolderContentCard = ({
  folder,
  onContextMenu,
}: {
  folder: Folder;
  onContextMenu?: (event: React.MouseEvent) => void;
}) => {
  const location = useLocation();
  const bulkSelection = useBulkSelectionContext<FolderContent>();

  const selectedRows = bulkSelection?.selectedRows ?? [];
  const isSelected = selectedRows.some(r => r.id === folder.id);
  const singleItemToDrag = useMemo(() => [{ ...folder, contentType: 'folder' } as FolderContent], [folder]);
  const contentsToDrag = isSelected && selectedRows.length > 0 ? selectedRows : singleItemToDrag;

  const {
    attributes,
    listeners,
    setNodeRef: setDragReference,
    isDragging,
  } = useDraggable({
    id: `drag-card-${folder.id}`,
    data: { contents: contentsToDrag },
  });

  const { setNodeRef: setDropReference, isOver } = useDroppable({
    id: `drop-card-${folder.id}`,
    data: { type: 'folder-drop-target', folderId: folder.id, folderName: folder.name },
  });

  const setNodeReference = (node: HTMLElement | null) => {
    setDragReference(node);
    setDropReference(node);
  };

  const folderLinkTo = useMemo(
    () => ({ pathname: location.pathname, search: `?folderId=${folder.id}` }),
    [location.pathname, folder.id]
  );

  const cardSx = useMemo(
    () => ({
      ...DND_NO_SELECT_STYLES,
      ...(isDragging && { opacity: DND_OPACITY.dragging }),
    }),
    [isDragging]
  );

  return (
    <StyledCard
      data-id={folder.id}
      data-field='name'
      data-dnd-drop-target={String(isOver)}
      onContextMenu={onContextMenu}
      ref={setNodeReference}
      {...attributes}
      {...listeners}
      sx={cardSx}
    >
      <FolderCardVisual folder={folder} linkTo={folderLinkTo} />
    </StyledCard>
  );
};
